import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import axios from "axios";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";

function AddGift() {
  // URL parameter handling
  const [searchParams] = useSearchParams();
  const giftId = searchParams.get("id");

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [subCategoryData, setSubCategoryData] = useState({});
  const [logo, setLogo] = useState(null);
  const [price, setPrice] = useState("");
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState("");

  // UI state
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const [categoryData, setCategoryData] = useState([]);

  // Error/Success messages
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [titleError, setTitleError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [subCategoryError, setSubCategoryError] = useState("");
  const [logoError, setLogoError] = useState("");

  const navigate = useNavigate();

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch gift data if editing
  useEffect(() => {
    if (giftId) {
      fetchGiftData(giftId);
    }
  }, [giftId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APIURL}category/list?front_category=true`
      );
      if (response.data.status === "success") {
        const data = response.data.result.map((item) => item.category);
        setCategoryData(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setErrorMessage("Failed to load categories");
    }
  };

  const fetchGiftData = async (id) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_APIURL}gift/list`, {
        params: { gift_id: id, admin: true }
      });

      if (response.data.status === "success") {
        const giftData = response.data.result;
        setTitle(giftData.name);
        setUploadedLogoUrl(giftData.url);
        setPrice(giftData.price);
        setDescription(giftData.description || "");

        if (Array.isArray(giftData.category_id)) {
          // Extract unique parent categories
          const categories = [...new Set(
            giftData.category_id.map(cat => cat.category || cat.parentCategory)
          )];
          setSelectedCategories(categories);

          // Set subcategories
          setSelectedSubCategories(giftData.category_id.map(cat => ({
            id: cat._id || cat.id,
            subcategory: cat.subcategory || cat.name,
            parentCategory: cat.category || cat.parentCategory,
            _id: cat._id
          })));

          // Fetch subcategories for each category
          categories.forEach(category => fetchSubCategories(category));
        }
      }
    } catch (error) {
      console.error("Error fetching gift data:", error);
      setErrorMessage("Failed to load gift data");
    }
  };

  const fetchSubCategories = async (category) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APIURL}category/list?category=${category}`
      );
      const data = response.data.result.map(item => ({
        id: item._id,
        subcategory: item.subcategory,
        parentCategory: category
      }));
      setSubCategoryData(prev => ({
        ...prev,
        [category]: data
      }));
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      const updatedCategories = [...selectedCategories, category];
      setSelectedCategories(updatedCategories);
      fetchSubCategories(category);
    }
  };

  const handleRemoveCategory = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
    setSelectedSubCategories(selectedSubCategories.filter(
      subCat => subCat.parentCategory !== categoryToRemove
    ));
  };

  const handleSubCategorySelect = (subcategory) => {
    if (!selectedSubCategories.find(sc => sc.id === subcategory.id)) {
      setSelectedSubCategories([...selectedSubCategories, subcategory]);
    }
  };

  const handleRemoveSubCategory = (subcategoryId) => {
    setSelectedSubCategories(selectedSubCategories.filter(
      sc => sc.id !== subcategoryId && sc._id !== subcategoryId
    ));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Clear previous errors
    setLogoError("");

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setLogoError("Please select a valid image file (JPEG, PNG, or GIF)");
      return;
    }

    const reader = new FileReader();
    reader.onload = function (event) {
      const img = new Image();
      img.src = event.target.result;

      img.onload = function () {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set max dimensions
        const maxWidth = 800;
        const maxHeight = 600;

        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob((blob) => {
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });
          setLogo(resizedFile);
          setUploadedLogoUrl(""); // Clear the old URL when new file is selected
        }, file.type, 0.8);
      };
    };
    reader.readAsDataURL(file);
  };

  const fileUpload = async () => {
    if (!logo) return null;

    const formData = new FormData();
    formData.append("file", logo);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APIURL}file/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      if (response.data.status === "success") {
        return response.data.result.files[0].file_url;
      }
      throw new Error("File upload failed");
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const validateForm = () => {
    let isValid = true;

    // Reset errors
    setTitleError("");
    setCategoryError("");
    setSubCategoryError("");
    setLogoError("");

    if (!title.trim()) {
      setTitleError("Title is required");
      isValid = false;
    }

    if (selectedCategories.length === 0) {
      setCategoryError("At least one category is required");
      isValid = false;
    }

    if (selectedSubCategories.length === 0) {
      setSubCategoryError("At least one subcategory is required");
      isValid = false;
    }

    if (!logo && !uploadedLogoUrl) {
      setLogoError("Image is required");
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsFormProcessing(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (!validateForm()) {
        setIsFormProcessing(false);
        return;
      }

      let logoUrl = uploadedLogoUrl;
      if (logo) {
        try {
          logoUrl = await fileUpload();
        } catch (error) {
          setErrorMessage("Failed to upload image");
          setIsFormProcessing(false);
          return;
        }
      }

      const payload = {
        name: title,
        description,
        url: logoUrl,
        price,
        gift_id: giftId || "",
        categories: selectedCategories,
        category_id: selectedSubCategories
          .flatMap(subCat => [subCat._id, subCat.id])
          .filter(Boolean)
          .filter(id => id.trim() !== "")
          .join(",")
      };

      // Add business_id if available
      const businessId = localStorage.getItem("business_id");
      const signupStatus = localStorage.getItem("signup");
      if (businessId && signupStatus === "user") {
        payload.business_id = businessId;
      }

      const apiUrl = giftId
        ? `${process.env.REACT_APP_APIURL}gift/update`
        : `${process.env.REACT_APP_APIURL}gift/create`;

      const response = await axios.post(apiUrl, payload);

      if (response.data.status === "success") {
        setSuccessMessage(giftId ? "Gift updated successfully!" : "Gift added successfully!");
        setTimeout(() => navigate("/gift"), 2000);
      } else {
        setErrorMessage("Failed to save gift");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage("Failed to process request");
    } finally {
      setIsFormProcessing(false);
    }
  };

  return (
    <div className="wrapper">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0 txt_lft">{giftId ? "Edit Gift" : "Add Gift"}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <form className="form-horizontal" onSubmit={handleSubmit}>
                      <div className="card-body">
                        {/* Title Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Title<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="text"
                              className="form-control"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                            />
                            {titleError && (
                              <small className="text-danger">{titleError}</small>
                            )}
                          </div>
                        </div>

                       
                        {/* Categories Selection */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Categories<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <select
                              className="form-control mb-2"
                              onChange={(e) => handleCategorySelect(e.target.value)}
                              value=""
                            >
                              <option value="">Select categories</option>
                              {categoryData
                                .filter(cat => !selectedCategories.includes(cat))
                                .map((category, index) => (
                                  <option key={index} value={category}>
                                    {category}
                                  </option>
                                ))}
                            </select>

                            {/* Selected Categories Display */}
                            <div className="selected-items mt-2">
                              {selectedCategories.map((category, index) => (
                                <div key={index} className="badge bg-primary p-2 m-1">
                                  {category}
                                  <button
                                    type="button"
                                    className="btn btn-link text-white p-0 ml-2"
                                    onClick={() => handleRemoveCategory(category)}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                            {categoryError && (
                              <small className="text-danger">{categoryError}</small>
                            )}
                          </div>
                        </div>

                        {/* Subcategories Selection */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Subcategories<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            {selectedCategories.map((category, index) => (
                              <div key={index} className="mb-2">
                                <label className="text-muted mb-1">{category}</label>
                                <select
                                  className="form-control"
                                  onChange={(e) => {
                                    const selected = subCategoryData[category]?.find(
                                      sc => sc.id === e.target.value
                                    );
                                    if (selected) {
                                      handleSubCategorySelect({
                                        ...selected,
                                        parentCategory: category,
                                      });
                                    }
                                  }}
                                  value=""
                                >
                                  <option value="">
                                    Select subcategories for {category}
                                  </option>
                                  {subCategoryData[category]?.map(
                                    (subCat, idx) => (
                                      <option key={idx} value={subCat.id}>
                                        {subCat.subcategory}
                                      </option>
                                    )
                                  )}
                                </select>
                              </div>
                            ))}

                            {/* Selected Subcategories Display */}
                            <div className="selected-items mt-2">
                              {selectedSubCategories.map((subCategory) => (
                                <div
                                  key={subCategory.id || subCategory._id}
                                  className="badge bg-info p-2 m-1"
                                >
                                  {subCategory.subcategory}
                                  <button
                                    type="button"
                                    className="btn btn-link text-white p-0 ml-2"
                                    onClick={() =>
                                      handleRemoveSubCategory(
                                        subCategory.id || subCategory._id
                                      )
                                    }
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>

                            {subCategoryError && (
                              <small className="text-danger">
                                {subCategoryError}
                              </small>
                            )}
                          </div>
                        </div>

                        {/* Image Upload */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Image<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <div className="position-relative">
                              <label className="btn-bs-file btn btn-primary">
                                Browse
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleFileChange}
                                  style={{ display: 'none' }}
                                />
                              </label>
                            </div>
                            {(logo || uploadedLogoUrl) && (
                              <div className="product_imgdiv mt-3">
                                <img
                                  src={logo ? URL.createObjectURL(logo) : uploadedLogoUrl}
                                  alt="Gift Logo"
                                  className="img-fluid"
                                  style={{ maxHeight: '200px' }}
                                />
                                <div className="product_imgdel">
                                  <X
                                    className="cursor-pointer"
                                    onClick={() => {
                                      setLogo(null);
                                      setUploadedLogoUrl("");
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            {logoError && (
                              <small className="text-danger">{logoError}</small>
                            )}
                          </div>
                        </div>

                        {/* Price Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Price ₹<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="number"
                              className="form-control"
                              value={price}
                              onChange={(e) => setPrice(e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>

                        {/* Error/Success Messages */}
                        {errorMessage && (
                          <div className="alert alert-danger" role="alert">
                            {errorMessage}
                          </div>
                        )}
                        {successMessage && (
                          <div className="alert alert-success" role="alert">
                            {successMessage}
                          </div>
                        )}
                      </div>

                      {/* Form Submit */}
                      <div className="card-footer text-center">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isFormProcessing}
                        >
                          {isFormProcessing
                            ? "Processing..."
                            : giftId
                              ? "Update"
                              : "Add"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default AddGift;